import { useState, useEffect } from 'react';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';

type GuardianMode = 'ACTIVE' | 'MONITOR' | 'OFF';

interface GuardianStatus {
  mode: string;
  totalRules: number;
  activeRules: number;
  monitorRules: number;
  lastEvaluation: Date | string | null;
}

function formatTimeAgo(date: Date | string | null): string {
  if (!date) return 'Never';
  const d = typeof date === 'string' ? new Date(date) : date;
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function normMode(m: string): GuardianMode {
  const upper = m.toUpperCase();
  if (upper === 'ACTIVE' || upper === 'active') return 'ACTIVE';
  if (upper === 'MONITOR' || upper === 'monitor') return 'MONITOR';
  return 'OFF';
}

const MODE_CYCLE: GuardianMode[] = ['ACTIVE', 'MONITOR', 'OFF'];

function Skeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 h-28" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 h-36" />
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 h-36" />
      </div>
    </div>
  );
}

export function StatusPanel() {
  const [status, setStatus] = useState<GuardianStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<GuardianMode>('OFF');

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    trpc.guardian.status.query({ tenantId: user.tenantId })
      .then(data => {
        setStatus(data);
        setMode(normMode(data.mode));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton />;

  const nextMode = MODE_CYCLE[(MODE_CYCLE.indexOf(mode) + 1) % MODE_CYCLE.length];

  return (
    <div className="space-y-4">
      {/* Status Bar */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Mode indicator */}
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex items-center justify-center w-14 h-14">
            {mode === 'ACTIVE' && (
              <>
                <span className="absolute w-14 h-14 rounded-full bg-emerald-500 opacity-20 animate-ping" />
                <span className="w-10 h-10 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/30" />
              </>
            )}
            {mode === 'MONITOR' && (
              <span className="w-10 h-10 rounded-full bg-amber-400 shadow-lg shadow-amber-400/30" />
            )}
            {mode === 'OFF' && (
              <span className="w-10 h-10 rounded-full bg-slate-600" />
            )}
          </div>
          <div>
            <div className={`text-3xl font-black tracking-tight ${
              mode === 'ACTIVE' ? 'text-emerald-400' :
              mode === 'MONITOR' ? 'text-amber-400' :
              'text-slate-500'
            }`}>
              {mode}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Last evaluated {formatTimeAgo(status?.lastEvaluation ?? null)}
            </div>
          </div>
        </div>

        {/* Toggle (UI only — no backend call) */}
        <button
          onClick={() => setMode(nextMode)}
          className="px-5 py-2.5 rounded-lg border border-slate-700 text-sm font-semibold text-slate-300 hover:border-slate-500 hover:text-white transition-colors"
        >
          Switch to {nextMode}
        </button>
      </div>

      {/* Two stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Rules summary */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Rules Summary</div>
          <div className="flex items-end gap-1 mb-4">
            <span className="text-5xl font-black text-slate-100">{status?.totalRules ?? 0}</span>
            <span className="text-lg text-slate-500 mb-1.5">total rules</span>
          </div>
          <div className="flex gap-6">
            <div>
              <div className="text-xl font-bold text-emerald-400">{status?.activeRules ?? 0}</div>
              <div className="text-xs text-slate-500">Active</div>
            </div>
            <div>
              <div className="text-xl font-bold text-amber-400">{status?.monitorRules ?? 0}</div>
              <div className="text-xs text-slate-500">Monitor only</div>
            </div>
          </div>
        </div>

        {/* Last evaluation */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Last Evaluation</div>
          <div className="flex items-end gap-1 mb-4">
            <span className="text-3xl font-black text-slate-100">
              {status?.lastEvaluation ? formatTimeAgo(status.lastEvaluation) : 'Never'}
            </span>
          </div>
          <div className="text-xs text-slate-500">
            {status?.lastEvaluation
              ? `Guardian last ran ${formatTimeAgo(status.lastEvaluation)}`
              : 'No evaluations recorded yet'}
          </div>
        </div>
      </div>
    </div>
  );
}
